import { type FormEvent, useState } from "react";
import { Button } from "@/ui/brand-button";
import { env } from "@/env";
import {
  selectOrganization as selectAuthOrganization,
  sendMagicCode,
  verifyMagicCode,
  type OrganizationOption,
} from "@/data/auth";

interface LoginLocation {
  pathname: string;
  search: string;
  hash: string;
}

interface LoginParams {
  loginHint: string;
  returnTo: string | null;
}

interface LoginScreenProps {
  apiBaseUrl?: string;
  location?: LoginLocation;
  onRedirect?: (url: string) => void;
}

export function parseLoginParams(location: LoginLocation): LoginParams {
  const search = new URLSearchParams(location.search);
  const explicitReturnTo = search.get("return_to");
  const normalizedExplicitReturnTo =
    explicitReturnTo && explicitReturnTo !== "/" ? explicitReturnTo : null;
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const fallbackReturnTo =
    location.pathname === "/login" || location.pathname === "/"
      ? null
      : currentPath || null;
  // The invite token is carried through `return_to`
  // (-> /invite?invitation_token=...), so the post-login redirect lands the
  // user on the invite-accept screen. Magic Auth itself is a plain sign-in
  // and must NOT forward an invitation token to WorkOS.
  return {
    loginHint: search.get("login_hint") ?? "",
    returnTo: normalizedExplicitReturnTo || fallbackReturnTo,
  };
}

function fallbackLocation(): LoginLocation {
  if (typeof window === "undefined") {
    return { pathname: "/", search: "", hash: "" };
  }
  return window.location;
}

export function LoginScreen({
  apiBaseUrl = env.API_BASE_URL,
  location = fallbackLocation(),
  onRedirect,
}: LoginScreenProps = {}) {
  const params = parseLoginParams(location);
  const [email, setEmail] = useState(params.loginHint);
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<"email" | "code" | "organization">(
    "email",
  );
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const redirect = (url: string) => {
    if (onRedirect) {
      onRedirect(url);
      return;
    }
    window.location.assign(url);
  };

  const sendCode = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await sendMagicCode(
        {
          email,
          returnTo: params.returnTo,
        },
        apiBaseUrl,
      );
      setStage("code");
      setCode("");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await verifyMagicCode(
        {
          email,
          code,
        },
        apiBaseUrl,
      );
      if (result.requires_organization_selection) {
        setOrganizations(result.organizations ?? []);
        setStage("organization");
        return;
      }
      if (result.ok && result.redirect_to) {
        redirect(result.redirect_to);
        return;
      }
      setMessage("Authentication failed");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const selectOrganization = async (organizationId: string) => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await selectAuthOrganization(organizationId, apiBaseUrl);
      if (result.ok && result.redirect_to) {
        redirect(result.redirect_to);
        return;
      }
      setMessage("Authentication failed");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onEmailSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void sendCode();
  };

  const onCodeSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void verifyCode();
  };

  return (
    <div className="anim-fade-in flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md space-y-8 rounded-[3px] border border-wiki-border bg-wiki-bg-2/80 p-9 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.6)]">
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <img
              src="/logo.png"
              alt=""
              width={28}
              height={28}
              className="rounded-[6px] invert"
              draggable={false}
            />
            <span className="text-[15px] font-semibold tracking-tight text-wiki-fg-1">
              Parable
            </span>
          </div>
          <h1 className="text-3xl leading-[1.15] font-semibold tracking-tight text-wiki-fg-1">
            Sign in
          </h1>
          <p className="text-[14px] leading-relaxed text-wiki-fg-2">
            Use your email to receive a one-time code.
          </p>
        </div>

        {stage === "email" ? (
          <form className="space-y-4" onSubmit={onEmailSubmit}>
            <label className="block space-y-2 text-sm text-wiki-fg-2">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.currentTarget.value)}
                autoComplete="email"
                required
                data-testid="login-email-input"
                className="h-11 w-full rounded-[3px] border border-wiki-border bg-wiki-bg px-3 text-[15px] text-wiki-fg-1 outline-none placeholder:text-wiki-fg-3 focus:border-wiki-accent"
              />
            </label>
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={busy}
              data-testid="login-send-code"
            >
              {busy ? "Sending..." : "Send code"}
            </Button>
          </form>
        ) : null}

        {stage === "code" ? (
          <form className="space-y-4" onSubmit={onCodeSubmit}>
            <div className="text-sm text-wiki-fg-2">{email}</div>
            <label className="block space-y-2 text-sm text-wiki-fg-2">
              <span>Code</span>
              <input
                type="text"
                value={code}
                onChange={(event) =>
                  setCode(
                    event.currentTarget.value.replace(/\D/g, "").slice(0, 6),
                  )
                }
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                data-testid="login-code-input"
                className="h-11 w-full rounded-[3px] border border-wiki-border bg-wiki-bg px-3 text-center text-[18px] tracking-[0.3em] text-wiki-fg-1 outline-none placeholder:text-wiki-fg-3 focus:border-wiki-accent"
              />
            </label>
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={busy || code.length !== 6}
              data-testid="login-verify-code"
            >
              {busy ? "Checking..." : "Continue"}
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => void sendCode()}
                disabled={busy}
                data-testid="login-resend-code"
              >
                Resend
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setStage("email");
                  setCode("");
                  setMessage(null);
                }}
                disabled={busy}
              >
                Change email
              </Button>
            </div>
          </form>
        ) : null}

        {stage === "organization" ? (
          <div className="space-y-3">
            <div className="text-sm font-medium text-wiki-fg-1">
              Choose workspace
            </div>
            <div className="space-y-2">
              {organizations.map((org) => (
                <button
                  key={org.organization_id}
                  type="button"
                  onClick={() => void selectOrganization(org.organization_id)}
                  disabled={busy}
                  data-testid="login-organization"
                  className="flex w-full items-center justify-between gap-3 rounded-[3px] border border-wiki-border bg-wiki-bg px-3 py-2.5 text-left text-sm text-wiki-fg-1 transition-colors hover:border-wiki-border-2 hover:bg-wiki-bg-3 disabled:opacity-50"
                >
                  <span className="min-w-0 truncate">{org.name}</span>
                  {org.workspace?.slug ? (
                    <span className="shrink-0 text-xs text-wiki-fg-3">
                      /{org.workspace.slug}/
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {message ? (
          <div
            className="rounded-[3px] border border-red-900/40 bg-red-950/30 px-3 py-2 text-sm text-red-200"
            role="alert"
          >
            {message}
          </div>
        ) : null}
      </div>
    </div>
  );
}
